export type EditorEntrySourceKind = "folder" | "shared" | "created" | "direct" | "guest"

export interface EditorEntrySource {
  kind: EditorEntrySourceKind
  href: string
  projectId?: string
}

export const EDITOR_ENTRY_SOURCE_KEY = "rudrise-editor-entry-source"

export function encodeEditorFolderPath(stack: Array<{ id: string; name: string }>): string {
  return stack
    .filter((item) => item.id && item.name)
    .map((item) => `${item.id}:${encodeURIComponent(item.name)}`)
    .join(",")
}

export function parseEditorFolderPath(path: string | null | undefined): Array<{ id: string; name: string }> {
  if (!path) return []
  return path
    .split(",")
    .map((part) => {
      const [id, name] = part.split(":")
      if (!id || !name) return null
      try {
        return { id, name: decodeURIComponent(name) }
      } catch {
        return { id, name }
      }
    })
    .filter((item): item is { id: string; name: string } => Boolean(item))
}

export function editorBackHrefFromContext(opts: {
  view?: "files" | "shared"
  stack?: Array<{ id: string; name: string }>
}): string {
  const params = new URLSearchParams()
  if (opts.view === "shared") params.set("view", "shared")
  const folderPath = encodeEditorFolderPath(opts.stack ?? [])
  if (folderPath) params.set("folderPath", folderPath)
  const qs = params.toString()
  return qs ? `/dashboard?${qs}` : "/dashboard"
}

export function resolveEditorLogoHref(isAuthenticated: boolean): string {
  return isAuthenticated ? "/dashboard" : "/"
}

export function resolveEditorBackHref(
  source: EditorEntrySource | null | undefined,
  isAuthenticated: boolean,
): string {
  if (!isAuthenticated) return "/"
  const href = source?.href?.trim()
  if (href && href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/editor")) {
    return href
  }
  return "/dashboard"
}

export function parseOriginFromLocation(url: string): EditorEntrySource | null {
  try {
    const parsed = new URL(url, "http://local.invalid")
    const path = parsed.pathname.replace(/\/+$/, "") || "/"
    const view = parsed.searchParams.get("view")
    const folderPath = parsed.searchParams.get("folderPath")

    if (path === "/editor" || path.startsWith("/editor/")) return null

    if (path.startsWith("/share/") || path === "/share") {
      return { kind: "shared", href: "/dashboard?view=shared" }
    }

    if (path.startsWith("/dashboard/create-new")) {
      return { kind: "created", href: "/dashboard" }
    }

    if (path === "/dashboard") {
      if (view === "shared") {
        return {
          kind: "shared",
          href: editorBackHrefFromContext({ view: "shared", stack: parseEditorFolderPath(folderPath) }),
        }
      }
      if (folderPath) {
        return {
          kind: "folder",
          href: editorBackHrefFromContext({ view: "files", stack: parseEditorFolderPath(folderPath) }),
        }
      }
      return { kind: "direct", href: "/dashboard" }
    }

    if (path === "/" || path.startsWith("/sign-") || path.startsWith("/about") || path.startsWith("/pricing")) {
      return { kind: "guest", href: "/" }
    }

    return null
  } catch {
    return null
  }
}

export function persistEditorEntrySource(source: EditorEntrySource): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(EDITOR_ENTRY_SOURCE_KEY, JSON.stringify(source))
  } catch {
    // ignore quota / private-mode errors
  }
}

export function loadEditorEntrySource(): EditorEntrySource | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem(EDITOR_ENTRY_SOURCE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<EditorEntrySource>
    if (!parsed || typeof parsed.href !== "string" || typeof parsed.kind !== "string") return null
    return {
      kind: parsed.kind as EditorEntrySourceKind,
      href: parsed.href,
      projectId: typeof parsed.projectId === "string" ? parsed.projectId : undefined,
    }
  } catch {
    return null
  }
}

export function rememberEditorEntrySource(source: EditorEntrySource): void {
  persistEditorEntrySource(source)
}

export function captureAndPersistEditorEntrySource(opts: {
  projectId?: string | null
  isAuthenticated: boolean
  pageHref?: string
  referrer?: string
}): EditorEntrySource {
  const fallback: EditorEntrySource = {
    kind: opts.isAuthenticated ? "direct" : "guest",
    href: opts.isAuthenticated ? "/dashboard" : "/",
    projectId: opts.projectId ?? undefined,
  }

  const stored = loadEditorEntrySource()
  const storedMatchesProject =
    Boolean(stored) &&
    (!opts.projectId || !stored?.projectId || stored.projectId === opts.projectId)
  if (stored && storedMatchesProject) {
    const next = { ...stored, projectId: opts.projectId ?? stored.projectId }
    persistEditorEntrySource(next)
    return next
  }

  const fromReferrer = opts.referrer ? parseOriginFromLocation(opts.referrer) : null
  const next: EditorEntrySource = fromReferrer
    ? { ...fromReferrer, projectId: opts.projectId ?? fromReferrer.projectId }
    : fallback

  persistEditorEntrySource(next)
  return next
}
